import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MyAELayout } from "../components/myae/Layout";
import { useAuth } from "../context/AuthContext";
import {
  getGroup, inviteToGroup, assignSection, removeMember, resetMemberSection,
  uploadSection, uploadFile, analyzeDraft, finalizeGroup, deleteGroup,
  groupExportUrl, uploadGroupBrief, generatePlan, applyPlan, importAllocation, errorMessage,
} from "../lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, Upload, Loader2, ArrowRight, CheckCircle2, Clock,
  Link as LinkIcon, Mail, Trash2, RefreshCw, FileDown, X,
  ChevronRight, Sparkles, FileText, Wand2, ChevronDown, Info, ClipboardPaste,
  AlertTriangle, Settings2,
} from "lucide-react";
import {
  MemberUploadCard, CoverageTracker, InviteLinkPanel,
} from "../components/groups/ContributionPanel";
import SectionOwnerPanel from "../components/groups/SectionOwnerPanel";
import SubmissionBuilder from "../components/groups/SubmissionBuilder";
import SectionManageMenu from "../components/groups/SectionManageMenu";
import ProjectLeadCard from "../components/groups/ProjectLeadCard";
import { setGroupLock } from "../lib/api";

const fmtDate = (s) =>
  s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const SectionStatusPill = ({ status }) => {
  const map = {
    pending: ["bg-zinc-100 text-zinc-600", "Pending"],
    uploaded: ["bg-indigo-100 text-indigo-700", "Uploaded"],
    analyzed: ["bg-emerald-100 text-emerald-700", "Analysed"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-600", status];
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

const InitialsAvatar = ({ name, email, size = 8 }) => {
  const s = (name || email || "?").trim();
  const initials = s.includes("@")
    ? s.split("@")[0][0]?.toUpperCase() || "?"
    : s.split(/[\s\-.]+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const px = `w-${size} h-${size}`;
  return (
    <div className={`${px} rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold shrink-0`}>
      {initials || "?"}
    </div>
  );
};

const InviteModal = ({ groupId, onClose, onInvited, sections }) => {
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (channel === "email" && !email.trim()) return toast.error("Enter an email address.");
    setBusy(true);
    try {
      const res = await inviteToGroup(groupId, {
        channel,
        email: email.trim(),
        name: name.trim(),
        section_id: sectionId,
      });
      setResult(res);
      if (channel === "email") toast.success("Invite sent by email.");
      onInvited();
    } catch (e) {
      toast.error(errorMessage(e, "Could not send invite."));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!result?.invite_url) return;
    navigator.clipboard.writeText(result.invite_url);
    toast.success("Invite link copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="invite-modal">
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <p className="font-display text-lg text-zinc-900">Invite a member</p>
          <button onClick={onClose} data-testid="invite-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        {!result ? (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-zinc-100 p-1">
              <button
                onClick={() => setChannel("email")}
                data-testid="invite-channel-email"
                className={`flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-md transition-all ${channel === "email" ? "bg-white text-zinc-900 shadow" : "text-zinc-500"}`}
              >
                <Mail size={13} /> Email invite
              </button>
              <button
                onClick={() => setChannel("link")}
                data-testid="invite-channel-link"
                className={`flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-md transition-all ${channel === "link" ? "bg-white text-zinc-900 shadow" : "text-zinc-500"}`}
              >
                <LinkIcon size={13} /> Shareable link
              </button>
            </div>

            {channel === "email" && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Email *</label>
                  <input
                    type="email"
                    data-testid="invite-email-input"
                    className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                    placeholder="teammate@university.ac.uk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">If they don&rsquo;t have a MyAE account, they&rsquo;ll be guided to sign up and joined automatically.</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Name (optional)</label>
                  <input
                    data-testid="invite-name-input"
                    className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                    placeholder="Alex"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Assign a section (optional)</label>
              <select
                data-testid="invite-section-select"
                className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none bg-white"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
              >
                <option value="">Assign later</option>
                {(sections || []).filter((s) => !s.assigned_to_member_id).map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="invite-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                {channel === "email" ? "Send invite" : "Generate link"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <p className="font-medium text-emerald-900 mb-1 text-sm">Invite ready ✓</p>
              <p className="text-xs text-emerald-800">Share this link with your teammate. They&rsquo;ll be joined automatically after signing in.</p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 flex items-center gap-2 break-all">
              <LinkIcon size={14} className="text-zinc-400 shrink-0" />
              <span className="text-xs text-zinc-700 flex-1" data-testid="invite-result-url">{result.invite_url}</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={copyLink} data-testid="invite-copy-link" className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-semibold hover:bg-zinc-800">
                Copy link
              </button>
              <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UploadSectionModal = ({ groupId, section, onClose, onUploaded }) => {
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const pickFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress({ filename: file.name, percent: 0 });
    try {
      const up = await uploadFile(file, (p) => setProgress({ filename: file.name, percent: p.percent }));
      setText(up.text || "");
      setFilename(file.name);
      toast.success(`Loaded "${file.name}"`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const submit = async () => {
    const words = (text.trim().match(/\b\w+\b/g) || []).length;
    if (words < 30) return toast.error("Add at least 30 words for a meaningful analysis.");
    setBusy(true);
    try {
      await uploadSection(groupId, section.id, {
        text: text.trim(),
        filename: filename || `${section.title}.txt`,
      });
      toast.success("Section uploaded — MyAE is analysing it now.");
      onUploaded();
    } catch (e) {
      toast.error(errorMessage(e, "Could not upload this section."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="upload-section-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <p className="font-display text-lg text-zinc-900">Upload: {section.title}</p>
            <p className="text-xs text-zinc-500 mt-0.5">Your section will be analysed individually and later merged with the rest.</p>
          </div>
          <button onClick={onClose} data-testid="upload-section-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt,.rtf,.pptx,.md"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="upload-section-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="upload-section-choose-file"
              className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-6 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
            >
              <Upload size={22} className="text-zinc-400" />
              <span>{filename ? `Selected: ${filename}` : "Click to upload a file (DOCX, PDF, TXT)"}</span>
              {progress && <span className="text-xs text-indigo-600">Uploading… {progress.percent}%</span>}
            </button>
          </div>
          <div className="relative flex items-center">
            <div className="flex-1 border-t border-zinc-200" />
            <span className="px-3 text-xs text-zinc-400">or paste text below</span>
            <div className="flex-1 border-t border-zinc-200" />
          </div>
          <textarea
            data-testid="upload-section-text"
            className="w-full min-h-[220px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
            placeholder={`Paste your ${section.title.toLowerCase()} content here…`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p className="text-xs text-zinc-500">{(text.trim().match(/\b\w+\b/g) || []).length} words</p>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !text.trim()}
            data-testid="upload-section-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Submit section
          </button>
        </div>
      </div>
    </div>
  );
};

const AssignPicker = ({ group, section, onPick, onClose }) => {
  return (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-picker">
    <div className="bg-white rounded-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
        <p className="font-medium text-sm">Assign &ldquo;{section.title}&rdquo; to…</p>
        <button onClick={onClose} className="text-zinc-400"><X size={16} /></button>
      </div>
      <div className="p-2 max-h-72 overflow-y-auto">
        {group.members.filter((m) => m.status === "joined" || m.status === "invited" || m.status === "pending_register").map((m) => (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            data-testid={`assign-pick-${m.id}`}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 text-left"
          >
            <InitialsAvatar name={m.name} email={m.email} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-900 truncate">{m.name || m.email}</p>
              <p className="text-xs text-zinc-500 truncate">{m.email} · {m.status.replace("_", " ")}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

const ComplexityChip = ({ level }) => {
  const map = {
    Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Medium: "bg-amber-50 text-amber-700 border-amber-200",
    High: "bg-rose-50 text-rose-700 border-rose-200",
  };
  if (!level) return null;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${map[level] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>{level}</span>;
};

const BriefModal = ({ groupId, existingBrief, existingFilename, onClose, onSaved }) => {
  const [text, setText] = useState(existingBrief || "");
  const [filename, setFilename] = useState(existingFilename || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const chooseFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      setText(up.text || "");
      setFilename(file.name);
      toast.success(`Loaded "${file.name}"`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that brief."));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (text.trim().length < 50) return toast.error("The brief looks too short. Please paste or upload the full brief.");
    setBusy(true);
    try {
      await uploadGroupBrief(groupId, { text: text.trim(), filename: filename || "Assignment Brief.txt" });
      toast.success("Brief saved. It will be used for every future analysis.");
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save brief."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="brief-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <p className="font-display text-lg text-zinc-900">Assignment Brief</p>
            <p className="text-xs text-zinc-500 mt-0.5">Shared reference for every individual + overall analysis.</p>
          </div>
          <button onClick={onClose} data-testid="brief-modal-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf,.md" className="hidden" onChange={(e) => chooseFile(e.target.files?.[0])} data-testid="brief-modal-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="brief-modal-choose-file"
            className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-5 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
          >
            <Upload size={20} className="text-zinc-400" />
            <span>{filename ? `Selected: ${filename}` : "Click to upload the brief"}</span>
          </button>
          <div className="relative flex items-center">
            <div className="flex-1 border-t border-zinc-200" />
            <span className="px-3 text-xs text-zinc-400">or paste below</span>
            <div className="flex-1 border-t border-zinc-200" />
          </div>
          <textarea
            data-testid="brief-modal-text"
            className="w-full min-h-[220px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
            placeholder="Paste your full assignment brief here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p className="text-xs text-zinc-500">{(text.trim().match(/\b\w+\b/g) || []).length} words</p>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
          <button onClick={save} disabled={busy || text.trim().length < 50} data-testid="brief-modal-save" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save brief
          </button>
        </div>
      </div>
    </div>
  );
};

const PlannerModal = ({ groupId, onClose, onApplied }) => {
  const [busy, setBusy] = useState(true);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    generatePlan(groupId, { include_members: true })
      .then((p) => setPlan(p))
      .catch((e) => { toast.error(errorMessage(e, "Could not generate a plan.")); onClose(); })
      .finally(() => setBusy(false));
  }, [groupId, onClose]);

  const updateSec = (i, field, val) =>
    setPlan((p) => ({ ...p, sections: p.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  const addSec = () => setPlan((p) => ({ ...p, sections: [...p.sections, { title: "", purpose: "", word_count: 500, complexity: "Medium", assigned_to_member_id: "" }] }));
  const removeSec = (i) => setPlan((p) => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));

  const apply = async () => {
    setBusy(true);
    try {
      await applyPlan(groupId, {
        sections: plan.sections.map((s) => ({
          title: s.title, purpose: s.purpose, word_count: s.word_count,
          complexity: s.complexity, assigned_to_member_id: s.assigned_to_member_id,
        })),
        replace_existing: true,
      });
      toast.success("Plan applied. Sections and allocations updated ⭐");
      onApplied();
    } catch (e) {
      toast.error(errorMessage(e, "Could not apply plan."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="planner-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <p className="font-display text-lg text-zinc-900 flex items-center gap-2">
              <Sparkles size={18} className="text-indigo-600" /> MyAE Project Planner ⭐
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">Review the plan, edit anything, then apply it to your group.</p>
          </div>
          <button onClick={onClose} data-testid="planner-modal-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        {busy && !plan ? (
          <div className="p-10 text-center">
            <Loader2 size={22} className="mx-auto text-indigo-500 animate-spin mb-3" />
            <p className="text-sm text-zinc-600">MyAE Project Planner is reading your brief…</p>
            <p className="text-xs text-zinc-500 mt-1">This usually takes 10–20 seconds.</p>
          </div>
        ) : plan ? (
          <>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Sparkles size={12} /> Detected
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-2">
                  <div><p className="text-[10px] uppercase text-zinc-500">Assignment</p><p className="text-sm font-medium text-zinc-900">{plan.detected_title || "Untitled"}</p></div>
                  <div><p className="text-[10px] uppercase text-zinc-500">Type</p><p className="text-sm font-medium text-zinc-900">{plan.detected_assessment_type || "—"}</p></div>
                  <div><p className="text-[10px] uppercase text-zinc-500">Target words</p><p className="text-sm font-medium text-zinc-900 tabular-nums">{plan.detected_word_count || "—"}</p></div>
                </div>
                {plan.rationale && <p className="text-xs text-zinc-700 italic">{plan.rationale}</p>}
              </div>

              {plan.fairness_warning && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="planner-fairness-warning">
                  ⚠ {plan.fairness_warning}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Sections ({plan.sections.length})</p>
                  <button onClick={addSec} data-testid="planner-modal-add" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">+ Add</button>
                </div>
                <div className="space-y-2">
                  {plan.sections.map((s, i) => (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`planner-modal-section-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-xs text-zinc-400 font-mono w-6 mt-2">{i + 1}.</span>
                        <div className="flex-1 space-y-2">
                          <div className="flex gap-2 items-start flex-wrap">
                            <input className="flex-1 min-w-[140px] rounded-md border border-zinc-300 px-2.5 py-1.5 text-sm font-medium focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none" placeholder="Section title" value={s.title} onChange={(e) => updateSec(i, "title", e.target.value)} data-testid={`planner-modal-title-${i}`} />
                            <input type="number" className="w-20 rounded-md border border-zinc-300 px-2 py-1.5 text-xs focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none tabular-nums" placeholder="words" value={s.word_count || ""} onChange={(e) => updateSec(i, "word_count", Number(e.target.value) || 0)} data-testid={`planner-modal-words-${i}`} />
                            <select className="rounded-md border border-zinc-300 px-2 py-1.5 text-xs bg-white focus:border-indigo-500 outline-none" value={s.complexity || "Medium"} onChange={(e) => updateSec(i, "complexity", e.target.value)} data-testid={`planner-modal-complexity-${i}`}>
                              <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
                            </select>
                            <select className="rounded-md border border-zinc-300 px-2 py-1.5 text-xs bg-white focus:border-indigo-500 outline-none" value={s.assigned_to_member_id || ""} onChange={(e) => updateSec(i, "assigned_to_member_id", e.target.value)} data-testid={`planner-modal-assignee-${i}`}>
                              <option value="">Unassigned</option>
                              {(plan.members || []).map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
                            </select>
                            <button onClick={() => removeSec(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`planner-modal-remove-${i}`}><X size={14} /></button>
                          </div>
                          {s.purpose && <p className="text-xs text-zinc-500">{s.purpose}</p>}
                          <div className="flex items-center gap-1.5">
                            <ComplexityChip level={s.complexity} />
                            <span className="text-[11px] text-zinc-500 tabular-nums">~{s.word_count || 0} words</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={apply} disabled={busy || plan.sections.length === 0} data-testid="planner-modal-apply" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Apply plan
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};




const AllocationImportModal = ({ groupId, members, hasBrief, onClose, onApplied }) => {
  // Step: "input" → "review"
  const [step, setStep] = useState("input");
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const chooseFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      setText(up.text || "");
      setFilename(file.name);
      toast.success(`Loaded "${file.name}"`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!text.trim() || text.trim().length < 20) {
      return toast.error("Paste the allocation, or upload the screenshot/document.");
    }
    setBusy(true);
    try {
      const r = await importAllocation(groupId, {
        text: text.trim(), filename: filename || "Allocation.txt",
      });
      setResult(r);
      setStep("review");
    } catch (e) {
      toast.error(errorMessage(e, "MyAE could not read that allocation."));
    } finally { setBusy(false); }
  };

  const updateSec = (i, field, val) =>
    setResult((r) => ({ ...r, sections: r.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  const removeSec = (i) =>
    setResult((r) => ({ ...r, sections: r.sections.filter((_, idx) => idx !== i) }));
  const addSec = () =>
    setResult((r) => ({ ...r, sections: [...r.sections, { title: "", purpose: "", assigned_to_member_id: "", assigned_to_name_raw: "" }] }));

  const apply = async () => {
    if (!result?.sections?.length) return toast.error("At least one part is required.");
    setBusy(true);
    try {
      await applyPlan(groupId, {
        sections: result.sections.map((s) => ({
          title: (s.title || "").trim() || "Part",
          purpose: (s.purpose || "").trim(),
          assigned_to_member_id: s.assigned_to_member_id || "",
        })),
        replace_existing: true,
      });
      toast.success("Allocation applied. Members and parts are set up ✨");
      onApplied();
    } catch (e) {
      toast.error(errorMessage(e, "Could not apply the allocation."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="allocation-import-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <p className="font-display text-lg text-zinc-900 flex items-center gap-2">
              <ClipboardPaste size={18} className="text-indigo-600" /> Import Existing Allocation
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {step === "input"
                ? "Paste the WhatsApp / Teams / Moodle allocation or upload the screenshot."
                : "Review the parts MyAE extracted, then apply."}
            </p>
          </div>
          <button onClick={onClose} data-testid="allocation-import-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>

        {step === "input" ? (
          <>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {!hasBrief && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-900">No assignment brief yet. Import will still work, but MyAE can&rsquo;t check whether the allocation covers every part of the brief.</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf,.md,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={(e) => chooseFile(e.target.files?.[0])} data-testid="allocation-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="allocation-choose-file"
                className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-5 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
              >
                <Upload size={20} className="text-zinc-400" />
                <span>{filename ? `Selected: ${filename}` : "Click to upload a screenshot, DOCX, PDF or text file"}</span>
                <span className="text-[11px] text-zinc-400">Screenshots are OCR&rsquo;d automatically</span>
              </button>
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-zinc-200" />
                <span className="px-3 text-xs text-zinc-400">or paste the allocation below</span>
                <div className="flex-1 border-t border-zinc-200" />
              </div>
              <textarea
                data-testid="allocation-text"
                className="w-full min-h-[220px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none font-mono"
                placeholder={"e.g.\n\nAlice — Introduction & Title\nBen — Background\nCharlie — Methodology\nDiana — Discussion\nEthan — Conclusion"}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <p className="text-xs text-zinc-500">{(text.trim().match(/\b\w+\b/g) || []).length} words</p>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button
                onClick={runImport}
                disabled={busy || text.trim().length < 20}
                data-testid="allocation-run-import"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Extract with MyAE
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {result.notes && (
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-2">
                  <Sparkles size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-indigo-900">{result.notes}</p>
                </div>
              )}

              {(result.coverage_warnings?.length > 0 || result.unmatched_member_names?.length > 0) && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1.5" data-testid="allocation-warnings">
                  <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5"><AlertTriangle size={12} /> Things to double-check</p>
                  {result.coverage_warnings?.map((w, i) => (
                    <p key={`c${i}`} className="text-xs text-amber-900">• {w}</p>
                  ))}
                  {result.unmatched_member_names?.length > 0 && (
                    <p className="text-xs text-amber-900">
                      • Names mentioned in the allocation but not yet in this group: <b>{result.unmatched_member_names.join(", ")}</b>. Invite them separately from the members panel.
                    </p>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Parts extracted ({result.sections.length})</p>
                  <button onClick={addSec} data-testid="allocation-add-part" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">+ Add</button>
                </div>
                <div className="space-y-2">
                  {result.sections.map((s, i) => (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`allocation-part-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-xs text-zinc-400 font-mono w-6 mt-2">{i + 1}.</span>
                        <div className="flex-1 space-y-2">
                          <div className="flex gap-2 items-start flex-wrap">
                            <input
                              className="flex-1 min-w-[140px] rounded-md border border-zinc-300 px-2.5 py-1.5 text-sm font-medium focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                              placeholder="Part title"
                              value={s.title}
                              onChange={(e) => updateSec(i, "title", e.target.value)}
                              data-testid={`allocation-title-${i}`}
                            />
                            <select
                              className="rounded-md border border-zinc-300 px-2 py-1.5 text-xs bg-white focus:border-indigo-500 outline-none min-w-[140px]"
                              value={s.assigned_to_member_id || ""}
                              onChange={(e) => updateSec(i, "assigned_to_member_id", e.target.value)}
                              data-testid={`allocation-assignee-${i}`}
                            >
                              <option value="">
                                {s.assigned_to_name_raw
                                  ? `Unmatched: ${s.assigned_to_name_raw}`
                                  : "Unassigned"}
                              </option>
                              {(members || []).map((m) => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                            <button onClick={() => removeSec(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`allocation-remove-${i}`}><X size={14} /></button>
                          </div>
                          <input
                            className="w-full rounded-md border border-zinc-200 px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                            placeholder="Short description of this part"
                            value={s.purpose || ""}
                            onChange={(e) => updateSec(i, "purpose", e.target.value)}
                            data-testid={`allocation-description-${i}`}
                          />
                          {s.assigned_to_name_raw && !s.assigned_to_member_id && (
                            <p className="text-[11px] text-amber-700">
                              &ldquo;{s.assigned_to_name_raw}&rdquo; wasn&rsquo;t found in your invited members. Pick a member above, or leave unassigned and invite them.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-between items-center">
              <button onClick={() => { setStep("input"); setResult(null); }} className="text-sm text-zinc-600 hover:text-zinc-900">← Re-import</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
                <button
                  onClick={apply}
                  disabled={busy || !result.sections.length}
                  data-testid="allocation-apply"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Confirm & apply
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [grp, setGrp] = useState(null);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showAllocationImport, setShowAllocationImport] = useState(false);
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadSec, setUploadSec] = useState(null);
  const [assignSec, setAssignSec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSubmissionBuilder, setShowSubmissionBuilder] = useState(false);
  const [openHistory, setOpenHistory] = useState(null);

  const refresh = async () => {
    try {
      const g = await getGroup(id);
      setGrp(g);
    } catch (e) {
      setError(errorMessage(e, "Could not load this group."));
    }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user, id]);

  // Poll while any section or the overall analysis is processing
  useEffect(() => {
    if (!grp) return;
    const anyProcessing =
      grp.overall_analysis_status === "processing" ||
      grp.draft_analysis_status === "processing" ||
      (grp.sections || []).some((s) => s.analysis_status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [grp]);

  if (!user) {
    return (
      <MyAELayout>
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <p className="text-zinc-700">Please sign in to view this group.</p>
        </div>
      </MyAELayout>
    );
  }

  if (error) {
    return (
      <MyAELayout>
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate("/groups")} className="text-sm text-indigo-600">Back to groups</button>
        </div>
      </MyAELayout>
    );
  }

  if (!grp) {
    return (
      <MyAELayout>
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <Loader2 size={22} className="mx-auto text-zinc-400 animate-spin" />
        </div>
      </MyAELayout>
    );
  }

  const totalSections = grp.sections.length;
  const uploadedCount = grp.sections.filter((s) => s.status === "uploaded" || s.status === "analyzed").length;
  const allUploaded = totalSections > 0 && uploadedCount === totalSections;
  const overallReady = grp.overall_analysis_id && grp.overall_analysis_status === "complete";
  const overallProcessing = grp.overall_analysis_status === "processing";

  const mySection = grp.sections.find((s) => s.assigned_to_member_id === grp.viewer_member_id && s.status === "pending");

  const doAssign = async (sectionId, memberId) => {
    setBusy(true);
    try {
      await assignSection(grp.id, { member_id: memberId, section_id: sectionId });
      await refresh();
      setAssignSec(null);
      toast.success("Section assigned");
    } catch (e) {
      toast.error(errorMessage(e, "Could not assign section."));
    } finally {
      setBusy(false);
    }
  };

  const doAnalyzeDraft = async () => {
    setBusy(true);
    try {
      await analyzeDraft(grp.id);
      toast.success("Draft analysis started — this will take a minute.");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "Could not start draft analysis."));
    } finally {
      setBusy(false);
    }
  };

  const doFinalize = async () => {
    setBusy(true);
    try {
      await finalizeGroup(grp.id);
      toast.success("Overall analysis started.");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "Could not start overall analysis."));
    } finally {
      setBusy(false);
    }
  };

  const doDeleteGroup = async () => {
    if (!confirm("Delete this group? Individual member analyses will remain in their own history, but the group itself will be gone.")) return;
    setBusy(true);
    try {
      await deleteGroup(grp.id);
      toast.success("Group deleted");
      navigate("/groups");
    } catch (e) {
      toast.error(errorMessage(e, "Could not delete this group."));
    } finally {
      setBusy(false);
    }
  };

  const doResetMember = async (memberId) => {
    if (!confirm("Reset this member's section? Their analysis stays in their history, but they'll need to re-upload for the group.")) return;
    setBusy(true);
    try {
      await resetMemberSection(grp.id, memberId);
      toast.success("Section reset. Ask them to re-upload.");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "Could not reset section."));
    } finally {
      setBusy(false);
    }
  };

  const doRemoveMember = async (memberId) => {
    if (!confirm("Remove this member from the group?")) return;
    setBusy(true);
    try {
      await removeMember(grp.id, memberId);
      toast.success("Member removed");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove member."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MyAELayout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="group-detail">
        {/* Header */}
        <button onClick={() => navigate("/groups")} className="text-sm text-indigo-600 hover:text-indigo-800 mb-4 inline-flex items-center gap-1">
          ← All groups
        </button>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl text-zinc-900 tracking-tight">{grp.title}</h1>
            <p className="text-zinc-600 mt-1 text-sm">
              {grp.module && <>{grp.module} · </>}
              {grp.is_owner ? "You own this group" : "You are a member"}
              {grp.deadline && <> · Due {fmtDate(grp.deadline)}</>}
            </p>
          </div>
          {grp.is_owner && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowInvite(true)}
                data-testid="invite-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-semibold hover:bg-zinc-800"
              >
                <UserPlus size={14} /> Invite member
              </button>
              <button
                onClick={doDeleteGroup}
                data-testid="group-delete-btn"
                className="text-zinc-400 hover:text-red-600 p-2"
                title="Delete group"
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Progress banner */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-6" data-testid="progress-banner">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-indigo-50 p-3">
              {allUploaded && overallReady ? (
                <CheckCircle2 size={22} className="text-emerald-600" />
              ) : allUploaded ? (
                <Sparkles size={22} className="text-indigo-600" />
              ) : (
                <Clock size={22} className="text-indigo-600" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-display text-lg text-zinc-900">
                {overallReady
                  ? "Your group assignment is ready to submit."
                  : allUploaded
                  ? overallProcessing
                    ? "All sections in — analysing the merged document…"
                    : "All sections received. Ready to run overall analysis."
                  : `${uploadedCount} of ${totalSections} sections received.`}
              </p>
              <p className="text-sm text-zinc-600 mt-1">
                {overallReady
                  ? "Review the combined document below, download the submission-ready file, or ask a member to re-upload their part."
                  : allUploaded
                  ? "MyAE is stitching the sections together and running the overall analysis. This normally takes 1–2 minutes."
                  : "Overall analysis will begin automatically once all required uploads are complete."}
              </p>

              {/* Progress bar */}
              <div className="mt-3 h-2 rounded-full bg-zinc-100 overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-all"
                  style={{ width: `${totalSections ? (uploadedCount / totalSections) * 100 : 0}%` }}
                />
              </div>

              {/* Owner actions */}
              {grp.is_owner && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {!allUploaded && uploadedCount > 0 && (
                    <button
                      onClick={doAnalyzeDraft}
                      disabled={busy || grp.draft_analysis_status === "processing"}
                      data-testid="analyze-draft-btn"
                      className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 px-3 py-1.5 text-xs font-semibold hover:bg-indigo-100 disabled:opacity-60"
                    >
                      {grp.draft_analysis_status === "processing" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      Analyse current draft
                    </button>
                  )}
                  {grp.draft_analysis_id && (
                    <button
                      onClick={() => navigate(`/report/${grp.draft_analysis_id}`)}
                      data-testid="view-draft-btn"
                      className="inline-flex items-center gap-2 rounded-lg bg-white border border-zinc-200 text-zinc-700 px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50"
                    >
                      View draft analysis <ArrowRight size={12} />
                    </button>
                  )}
                  {allUploaded && !overallReady && !overallProcessing && (
                    <button
                      onClick={doFinalize}
                      disabled={busy}
                      data-testid="finalize-btn"
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60"
                    >
                      <Sparkles size={12} /> Run overall analysis
                    </button>
                  )}
                  {overallReady && (
                    <>
                      <button
                        onClick={() => navigate(`/report/${grp.overall_analysis_id}`)}
                        data-testid="view-overall-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                      >
                        View overall report <ArrowRight size={12} />
                      </button>
                      <button
                        onClick={() => setShowSubmissionBuilder(true)}
                        data-testid="submission-builder-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-indigo-700 shadow-sm"
                      >
                        <FileDown size={12} /> Generate Final Submission
                      </button>
                    </>
                  )}
                  {!overallReady && grp.is_owner && (grp.sections || []).some((s) => s.status === "uploaded" || s.status === "analyzed") && (
                    <button
                      onClick={() => setShowSubmissionBuilder(true)}
                      data-testid="submission-builder-btn"
                      className="inline-flex items-center gap-2 rounded-lg bg-white border-2 border-indigo-500 text-indigo-700 px-3 py-1.5 text-xs font-semibold hover:bg-indigo-50"
                    >
                      <FileDown size={12} /> Generate Final Submission
                    </button>
                  )}
                </div>
              )}

              {/* Submission Builder inline panel */}
              {grp.is_owner && showSubmissionBuilder && (
                <SubmissionBuilder
                  group={grp}
                  onClose={() => setShowSubmissionBuilder(false)}
                  onGenerated={() => { setShowSubmissionBuilder(false); refresh(); }}
                />
              )}

              {/* Member CTAs */}
              {!grp.is_owner && mySection && (
                <div className="mt-4">
                  <button
                    onClick={() => setUploadSec(mySection)}
                    data-testid="upload-my-section-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700"
                  >
                    <Upload size={14} /> Upload my section: {mySection.title}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Version lock banner — visible to everyone when group is locked */}
        {grp.locked && (
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 mb-6 flex items-start justify-between gap-4" data-testid="group-locked-banner">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">🔒</div>
              <div>
                <p className="font-display text-base font-semibold text-emerald-900">Submission Pack generated · Group locked</p>
                <p className="text-sm text-emerald-800 mt-0.5">
                  Nobody can accidentally edit the final version.
                  {grp.locked_by_name ? <> Locked by {grp.locked_by_name}.</> : null}
                </p>
              </div>
            </div>
            {grp.is_owner && (
              <button
                onClick={async () => {
                  if (!window.confirm("Unlock the group? Members will be able to upload and edit sections again.")) return;
                  try { await setGroupLock(grp.id, false); toast.success("Group unlocked."); refresh(); }
                  catch (e) { toast.error(errorMessage(e)); }
                }}
                data-testid="group-unlock-btn"
                className="text-xs font-semibold text-emerald-800 bg-white border border-emerald-300 hover:bg-emerald-100 rounded-md px-3 py-1.5"
              >
                Unlock
              </button>
            )}
          </div>
        )}

        {/* AI-FIRST PRIMARY UI */}
        {grp.is_owner ? (
          <div className="space-y-5 mb-6">
            {/* AI Project Lead — the FIRST thing owners see: what still needs
                doing, estimated grade, top 3 improvements */}
            <ProjectLeadCard group={grp} />
            <InviteLinkPanel
              url={grp.public_invite_url}
              joined={grp.members.filter((m) => m.status === "joined").length}
              max={grp.max_members}
            />
            <CoverageTracker grp={grp} />
          </div>
        ) : (
          <div className="mb-6">
            <MemberUploadCard
              groupId={grp.id}
              hasBrief={grp.has_brief}
              briefTopic={grp.brief_topic}
              onDone={() => refresh()}
            />
          </div>
        )}

        {/* Advanced options — the classic manual workflow, hidden by default */}
        <div className="mb-6">
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="advanced-options-toggle"
            className="text-xs font-semibold text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1.5"
          >
            <Settings2 size={12} /> {showAdvanced ? "Hide" : "Show"} advanced options (brief, manual allocation, Project Planner)
            <ChevronDown size={12} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </button>
        </div>

        {showAdvanced && (
        <>
        {/* Brief panel — shared reference for every analysis */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 mb-6" data-testid="brief-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`rounded-full p-2 ${grp.has_brief ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                <FileText size={16} />
              </div>
              <div className="min-w-0">
                <p className="font-display text-base text-zinc-900 flex items-center gap-2">
                  Assignment Brief
                  {grp.has_brief ? (
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Loaded</span>
                  ) : (
                    <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">Missing</span>
                  )}
                </p>
                {grp.has_brief ? (
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {grp.brief_filename || "Brief"} · {grp.brief_word_count} words · used as the shared reference for every analysis.
                  </p>
                ) : (
                  <p className="text-xs text-zinc-500 mt-0.5">Upload the brief once — MyAE will use it for every individual and overall analysis so feedback stays consistent.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {grp.has_brief && (
                <button onClick={() => setBriefExpanded((v) => !v)} data-testid="brief-toggle-btn" className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
                  {briefExpanded ? "Hide" : "View brief"} <ChevronDown size={12} className={`transition-transform ${briefExpanded ? "rotate-180" : ""}`} />
                </button>
              )}
              {grp.is_owner && grp.status !== "analyzed" && grp.status !== "final" && (
                <button onClick={() => setShowBrief(true)} data-testid="brief-edit-btn" className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                  {grp.has_brief ? "Replace" : "Upload brief"} <ArrowRight size={11} />
                </button>
              )}
              {grp.is_owner && grp.has_brief && grp.status !== "analyzed" && grp.status !== "final" && (
                <button onClick={() => setShowPlanner(true)} data-testid="run-project-planner-btn" className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5 inline-flex items-center gap-1">
                  <Sparkles size={12} /> Run Project Planner ⭐
                </button>
              )}
            </div>
          </div>
          {briefExpanded && grp.has_brief && (
            <div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-3 max-h-56 overflow-y-auto" data-testid="brief-content">
              <pre className="whitespace-pre-wrap font-sans text-xs text-zinc-700 leading-relaxed">{grp.brief_text}</pre>
            </div>
          )}
        </div>

        {/* AI Allocation Import — owner only, before any uploads */}
        {grp.is_owner && grp.status !== "analyzed" && grp.status !== "final" && !grp.sections.some((s) => s.status !== "pending") && (
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 mb-6" data-testid="allocation-panel">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="rounded-full bg-indigo-50 p-2 text-indigo-600">
                  <ClipboardPaste size={16} />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-base text-zinc-900">Already agreed the split?</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Paste your WhatsApp / Teams / Moodle allocation (or drop in a screenshot) &mdash; MyAE extracts the parts and matches them to your invited members. Review before applying.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAllocationImport(true)}
                data-testid="allocation-import-btn"
                className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5 inline-flex items-center gap-1 shrink-0"
              >
                <ClipboardPaste size={12} /> Import allocation
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sections */}
          <div className="lg:col-span-2 space-y-3" data-testid="sections-panel">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Sections</p>
            {grp.sections.map((s, i) => {
              const isMine = s.assigned_to_member_id && s.assigned_to_member_id === grp.viewer_member_id;
              const canUpload = (isMine || grp.is_owner) && s.status === "pending";
              const showBanner = isMine && !!s.owner_request_message;
              return (
                <div key={s.id} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`section-row-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-zinc-400 font-mono tabular-nums">{i + 1}.</span>
                        <p className="font-display text-base text-zinc-900">{s.title}</p>
                        <SectionStatusPill status={s.analysis_status === "complete" ? "analyzed" : s.status} />
                        {s.complexity && <ComplexityChip level={s.complexity} />}
                        {s.planned_word_count > 0 && <span className="text-[11px] text-zinc-500 tabular-nums">~{s.planned_word_count}w</span>}
                        {s.version_count > 1 && (
                          <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5">
                            v{(s.versions || []).find((v) => v.is_current)?.version_number || s.version_count}
                          </span>
                        )}
                        {s.manually_overridden && (
                          <span
                            className="text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5"
                            title="Owner manually set this section — not AI-classified"
                            data-testid={`section-manual-override-${s.id}`}
                          >
                            Manually assigned
                          </span>
                        )}
                        {s.review_recommended && !s.manually_overridden && (
                          <span
                            className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                            title="AI wasn't fully confident — owner may want to review the section assignment"
                          >
                            AI review recommended
                          </span>
                        )}
                      </div>
                      {s.purpose && <p className="text-xs text-zinc-600 mb-1 italic">{s.purpose}</p>}
                      <p className="text-xs text-zinc-500">
                        {s.assigned_to_name ? `Assigned to ${s.assigned_to_name}` : "Not yet assigned"}
                        {s.uploaded_at && <> · Uploaded {fmtDate(s.uploaded_at)} · {s.word_count} words</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.analysis_id && (
                        <button
                          onClick={() => navigate(`/report/${s.analysis_id}`)}
                          data-testid={`section-view-${s.id}`}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                        >
                          Report <ChevronRight size={12} />
                        </button>
                      )}
                      {grp.is_owner && s.status === "pending" && !grp.locked && (
                        <button
                          onClick={() => setAssignSec(s)}
                          data-testid={`section-assign-${s.id}`}
                          className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md px-2 py-1"
                        >
                          Assign
                        </button>
                      )}
                      {canUpload && !grp.locked && (
                        <button
                          onClick={() => setUploadSec(s)}
                          data-testid={`section-upload-${s.id}`}
                          className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md px-3 py-1.5 inline-flex items-center gap-1"
                        >
                          <Upload size={12} /> Upload
                        </button>
                      )}
                      {grp.is_owner && (
                        <SectionManageMenu
                          group={grp}
                          section={s}
                          onChanged={refresh}
                          onOpenHistory={() => setOpenHistory(s.id)}
                        />
                      )}
                    </div>
                  </div>

                  {/* Re-upload banner for the assigned member */}
                  {showBanner && (
                    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid={`section-reupload-banner-${s.id}`}>
                      <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-amber-900">The group owner has asked you to re-upload this section.</p>
                        {s.owner_request_message && s.owner_request_message !== "The group owner has asked you to re-upload this section." && (
                          <p className="text-xs text-amber-800 mt-0.5">{s.owner_request_message}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Version history — opened on demand from the Manage menu */}
                  {grp.is_owner && openHistory === s.id && ((s.versions || []).length > 0 || !!s.analysis_id) && (
                    <SectionOwnerPanel group={grp} section={s} onChanged={refresh} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Members */}
          <div data-testid="members-panel">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              Members · {grp.members.filter((m) => m.status !== "removed").length}/{grp.max_members}
            </p>
            <div className="rounded-xl border border-zinc-200 bg-white divide-y divide-zinc-100">
              {grp.members.filter((m) => m.status !== "removed").map((m) => {
                const memberSections = grp.sections.filter((s) => s.assigned_to_member_id === m.id);
                const uploaded = memberSections.filter((s) => s.status !== "pending").length;
                return (
                  <div key={m.id} className="p-3 flex items-start gap-3" data-testid={`member-row-${m.id}`}>
                    <InitialsAvatar name={m.name} email={m.email} size={9} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-900 font-medium truncate">
                        {m.name || m.email} {m.role === "owner" && <span className="text-[10px] font-semibold uppercase text-indigo-600 ml-1">Owner</span>}
                      </p>
                      <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                      <p className="text-[11px] text-zinc-500 mt-1">
                        {m.status === "joined" ? `${uploaded}/${memberSections.length} section${memberSections.length === 1 ? "" : "s"} uploaded` :
                          m.status === "pending_register" ? "Waiting for account" :
                          m.status === "invited" ? "Invited" : m.status}
                      </p>
                    </div>
                    {grp.is_owner && m.role !== "owner" && (
                      <div className="flex flex-col gap-1">
                        {uploaded > 0 && (
                          <button
                            onClick={() => doResetMember(m.id)}
                            data-testid={`member-reset-${m.id}`}
                            className="text-[11px] text-zinc-500 hover:text-indigo-700 inline-flex items-center gap-1"
                            title="Ask member to re-upload"
                          >
                            <RefreshCw size={11} /> Reset
                          </button>
                        )}
                        <button
                          onClick={() => doRemoveMember(m.id)}
                          data-testid={`member-remove-${m.id}`}
                          className="text-[11px] text-zinc-400 hover:text-red-600 inline-flex items-center gap-1"
                          title="Remove member"
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </>
        )}
      </div>

      {showInvite && (
        <InviteModal
          groupId={grp.id}
          sections={grp.sections}
          onClose={() => setShowInvite(false)}
          onInvited={refresh}
        />
      )}
      {showBrief && (
        <BriefModal
          groupId={grp.id}
          existingBrief={grp.brief_text}
          existingFilename={grp.brief_filename}
          onClose={() => setShowBrief(false)}
          onSaved={() => { setShowBrief(false); refresh(); }}
        />
      )}
      {showPlanner && (
        <PlannerModal
          groupId={grp.id}
          onClose={() => setShowPlanner(false)}
          onApplied={() => { setShowPlanner(false); refresh(); }}
        />
      )}
      {showAllocationImport && (
        <AllocationImportModal
          groupId={grp.id}
          members={grp.members.filter((m) => m.status !== "removed").map((m) => ({ id: m.id, name: m.name || m.email }))}
          hasBrief={grp.has_brief}
          onClose={() => setShowAllocationImport(false)}
          onApplied={() => { setShowAllocationImport(false); refresh(); }}
        />
      )}
      {uploadSec && (
        <UploadSectionModal
          groupId={grp.id}
          section={uploadSec}
          onClose={() => setUploadSec(null)}
          onUploaded={() => { setUploadSec(null); refresh(); }}
        />
      )}
      {assignSec && (
        <AssignPicker
          group={grp}
          section={assignSec}
          onClose={() => setAssignSec(null)}
          onPick={(mid) => doAssign(assignSec.id, mid)}
        />
      )}
    </MyAELayout>
  );
}
